import { apiClient } from './apiClient';
import {
  SocialGroup,
  SocialGroupMembership,
  SocialEvent,
  EventRegistration,
  ForumPost,
  CommunityAnnouncement,
  ForumCategory,
  ForumTopic,
} from '@/hooks/shared/useSocialFeatures';
import type {
  EventComment,
  CreateCommentData,
  UpdateCommentData,
  CommentReportReason,
  Attendee,
} from '@/types/shared/social';

type GroupMembership = SocialGroupMembership;
type EventRSVP = EventRegistration;

interface ForumReply {
  id: string;
  tenantId: string;
  postId: string;
  parentReplyId?: string;
  authorId: string;
  authorName: string;
  content: string;
  attachments: string[];
  likeCount: number;
  status: 'active' | 'edited' | 'deleted' | 'flagged';
  moderationNotes?: string;
  ipAddress?: string;
  createdAt: string;
  updatedAt: string;
}

class SocialService {
  // Social Groups
  async getSocialGroups(
    tenantId?: string,
    filters?: {
      category?: string;
      groupType?: string;
      membershipType?: string;
    },
  ): Promise<SocialGroup[]> {
    return apiClient.get<SocialGroup[]>('/social/groups', {
      tenantId,
      ...filters,
    });
  }

  async getSocialGroup(groupId: string): Promise<SocialGroup> {
    return apiClient.get<SocialGroup>(`/social/groups/${groupId}`);
  }

  async createSocialGroup(
    groupData: Omit<
      SocialGroup,
      'id' | 'memberCount' | 'postCount' | 'eventCount' | 'createdAt' | 'updatedAt'
    >,
  ): Promise<SocialGroup> {
    return apiClient.post<SocialGroup>('/social/groups', groupData);
  }

  // Group management - Methods expected by hooks
  async createGroup(
    tenantId: string,
    groupData: Omit<
      SocialGroup,
      | 'id'
      | 'tenantId'
      | 'memberCount'
      | 'activityLevel'
      | 'isFeatured'
      | 'socialMediaLinks'
      | 'tags'
      | 'interests'
      | 'createdAt'
      | 'updatedAt'
    >,
  ): Promise<SocialGroup> {
    return apiClient.post('/social/groups', { tenantId, ...groupData });
  }

  async updateSocialGroup(groupId: string, updates: Partial<SocialGroup>): Promise<SocialGroup> {
    return apiClient.put<SocialGroup>(`/social/groups/${groupId}`, updates);
  }

  async deleteSocialGroup(groupId: string): Promise<void> {
    await apiClient.delete(`/social/groups/${groupId}`);
  }

  // Group Memberships
  async getGroupMemberships(groupId: string): Promise<GroupMembership[]> {
    return apiClient.get<GroupMembership[]>(`/social/groups/${groupId}/members`);
  }

  async joinGroup(groupId: string, userId: string, message?: string): Promise<GroupMembership> {
    return apiClient.post<GroupMembership>(`/social/groups/${groupId}/join`, {
      userId,
      joinMessage: message,
    });
  }

  async leaveGroup(groupId: string, userId: string, reason?: string): Promise<void> {
    await apiClient.post(`/social/groups/${groupId}/leave`, {
      userId,
      leaveReason: reason,
    });
  }

  async updateMembership(
    membershipId: string,
    updates: Partial<GroupMembership>,
  ): Promise<GroupMembership> {
    return apiClient.put<GroupMembership>(`/social/memberships/${membershipId}`, updates);
  }

  // Social Events
  async getSocialEvents(
    tenantId?: string,
    filters?: {
      groupId?: string;
      category?: string;
      eventType?: string;
      startDate?: string;
      endDate?: string;
    },
  ): Promise<SocialEvent[]> {
    return apiClient.get<SocialEvent[]>('/social/events', {
      tenantId,
      ...filters,
    });
  }

  async getSocialEvent(eventId: string): Promise<SocialEvent> {
    return apiClient.get<SocialEvent>(`/social/events/${eventId}`);
  }

  async createSocialEvent(
    eventData: Omit<SocialEvent, 'id' | 'rsvpCount' | 'attendeeCount' | 'createdAt' | 'updatedAt'>,
  ): Promise<SocialEvent> {
    return apiClient.post<SocialEvent>('/social/events', eventData);
  }

  // Alias method expected by hooks
  async createEvent(
    tenantId: string,
    eventData: Omit<
      SocialEvent,
      | 'id'
      | 'tenantId'
      | 'timezone'
      | 'registeredCount'
      | 'attendedCount'
      | 'waitlistCount'
      | 'attachments'
      | 'createdAt'
      | 'updatedAt'
    >,
  ): Promise<SocialEvent> {
    return apiClient.post('/social/events', { tenantId, ...eventData });
  }

  async updateSocialEvent(eventId: string, updates: Partial<SocialEvent>): Promise<SocialEvent> {
    return apiClient.put<SocialEvent>(`/social/events/${eventId}`, updates);
  }

  async deleteSocialEvent(eventId: string): Promise<void> {
    await apiClient.delete(`/social/events/${eventId}`);
  }

  // Event RSVPs
  async getEventRSVPs(eventId: string): Promise<EventRSVP[]> {
    return apiClient.get<EventRSVP[]>(`/social/events/${eventId}/rsvps`);
  }

  async rsvpToEvent(
    eventId: string,
    rsvpData: Omit<EventRSVP, 'id' | 'eventId' | 'createdAt' | 'updatedAt'>,
  ): Promise<EventRSVP> {
    return apiClient.post<EventRSVP>(`/social/events/${eventId}/rsvp`, rsvpData);
  }

  // Alias method expected by hooks
  async registerForEvent(
    eventId: string,
    userId: string,
    registrationData?: Partial<EventRSVP>,
  ): Promise<EventRSVP> {
    const fullRegistrationData = {
      userId,
      tenantId: registrationData?.tenantId || '',
      registrationStatus: 'registered' as const,
      registrationType: 'individual' as const,
      attendeeCount: 1,
      paymentRequired: false,
      paymentAmount: 0,
      paymentStatus: 'waived' as const,
      checkedIn: false,
      confirmationSent: false,
      reminderSent: false,
      followUpSent: false,
      refundAmount: 0,
      registeredAt: new Date().toISOString(),
      ...registrationData,
    };
    return this.rsvpToEvent(eventId, fullRegistrationData);
  }

  async updateRSVP(rsvpId: string, updates: Partial<EventRSVP>): Promise<EventRSVP> {
    return apiClient.put<EventRSVP>(`/social/rsvps/${rsvpId}`, updates);
  }

  async deleteRSVP(rsvpId: string): Promise<void> {
    await apiClient.delete(`/social/rsvps/${rsvpId}`);
  }

  // Event Attendees
  async getEventAttendees(
    eventId: string,
    filters?: {
      status?: string;
      checkInStatus?: boolean;
    },
  ): Promise<Attendee[]> {
    return apiClient.get<Attendee[]>(`/social/events/${eventId}/attendees`, filters);
  }

  // Event Likes
  async likeEvent(eventId: string, userId: string): Promise<void> {
    await apiClient.post(`/social/events/${eventId}/like`, { userId });
  }

  async unlikeEvent(eventId: string, userId: string): Promise<void> {
    await apiClient.delete(`/social/events/${eventId}/like/${userId}`);
  }

  async getEventLikes(eventId: string): Promise<string[]> {
    return apiClient.get<string[]>(`/social/events/${eventId}/likes`);
  }

  // Event Comments
  async getEventComments(
    eventId: string,
    filters?: {
      parentId?: string | null;
      status?: string;
    },
  ): Promise<EventComment[]> {
    return apiClient.get<EventComment[]>(`/social/events/${eventId}/comments`, filters);
  }

  async createEventComment(eventId: string, commentData: CreateCommentData): Promise<EventComment> {
    return apiClient.post<EventComment>(`/social/events/${eventId}/comments`, commentData);
  }

  async updateEventComment(commentId: string, updates: UpdateCommentData): Promise<EventComment> {
    return apiClient.put<EventComment>(`/social/comments/${commentId}`, updates);
  }

  async deleteEventComment(commentId: string): Promise<void> {
    await apiClient.delete(`/social/comments/${commentId}`);
  }

  async reportComment(
    commentId: string,
    userId: string,
    reason: CommentReportReason,
    description?: string,
  ): Promise<void> {
    await apiClient.post(`/social/comments/${commentId}/report`, {
      reporterId: userId,
      reason,
      description,
      timestamp: new Date().toISOString(),
    });
  }

  async likeComment(commentId: string, userId: string): Promise<void> {
    await apiClient.post(`/social/comments/${commentId}/like`, { userId });
  }

  async unlikeComment(commentId: string, userId: string): Promise<void> {
    await apiClient.delete(`/social/comments/${commentId}/like/${userId}`);
  }

  // Forum Posts
  async getForumPosts(
    tenantId?: string,
    filters?: {
      groupId?: string;
      category?: string;
      authorId?: string;
      status?: string;
    },
  ): Promise<ForumPost[]> {
    return apiClient.get<ForumPost[]>('/social/forum/posts', {
      tenantId,
      ...filters,
    });
  }

  async getForumPost(postId: string): Promise<ForumPost> {
    return apiClient.get<ForumPost>(`/social/forum/posts/${postId}`);
  }

  async createForumPost(
    postData: Omit<
      ForumPost,
      | 'id'
      | 'replyCount'
      | 'likeCount'
      | 'viewCount'
      | 'lastReplyAt'
      | 'lastReplyBy'
      | 'createdAt'
      | 'updatedAt'
    >,
  ): Promise<ForumPost> {
    return apiClient.post<ForumPost>('/social/forum/posts', postData);
  }

  async updateForumPost(postId: string, updates: Partial<ForumPost>): Promise<ForumPost> {
    return apiClient.put<ForumPost>(`/social/forum/posts/${postId}`, updates);
  }

  async deleteForumPost(postId: string): Promise<void> {
    await apiClient.delete(`/social/forum/posts/${postId}`);
  }

  async likeForumPost(postId: string, userId: string): Promise<ForumPost> {
    return apiClient.post<ForumPost>(`/social/forum/posts/${postId}/like`, {
      userId,
    });
  }

  // Forum Replies
  async getForumReplies(postId: string): Promise<ForumReply[]> {
    return apiClient.get<ForumReply[]>(`/social/forum/posts/${postId}/replies`);
  }

  async createForumReply(
    postId: string,
    replyData: Omit<ForumReply, 'id' | 'postId' | 'likeCount' | 'createdAt' | 'updatedAt'>,
  ): Promise<ForumReply> {
    return apiClient.post<ForumReply>(`/social/forum/posts/${postId}/replies`, replyData);
  }

  async updateForumReply(replyId: string, updates: Partial<ForumReply>): Promise<ForumReply> {
    return apiClient.put<ForumReply>(`/social/forum/replies/${replyId}`, updates);
  }

  async deleteForumReply(replyId: string): Promise<void> {
    await apiClient.delete(`/social/forum/replies/${replyId}`);
  }

  async likeForumReply(replyId: string, userId: string): Promise<ForumReply> {
    return apiClient.post<ForumReply>(`/social/forum/replies/${replyId}/like`, {
      userId,
    });
  }

  // Community Announcements
  async getCommunityAnnouncements(
    tenantId?: string,
    filters?: {
      category?: string;
      priority?: string;
      status?: string;
      authorId?: string;
    },
  ): Promise<CommunityAnnouncement[]> {
    return apiClient.get<CommunityAnnouncement[]>('/social/announcements', {
      tenantId,
      ...filters,
    });
  }

  async getCommunityAnnouncement(announcementId: string): Promise<CommunityAnnouncement> {
    return apiClient.get<CommunityAnnouncement>(`/social/announcements/${announcementId}`);
  }

  async createCommunityAnnouncement(
    announcementData: Omit<
      CommunityAnnouncement,
      'id' | 'viewCount' | 'likeCount' | 'shareCount' | 'createdAt' | 'updatedAt'
    >,
  ): Promise<CommunityAnnouncement> {
    return apiClient.post<CommunityAnnouncement>('/social/announcements', announcementData);
  }

  async updateCommunityAnnouncement(
    announcementId: string,
    updates: Partial<CommunityAnnouncement>,
  ): Promise<CommunityAnnouncement> {
    return apiClient.put<CommunityAnnouncement>(`/social/announcements/${announcementId}`, updates);
  }

  async deleteCommunityAnnouncement(announcementId: string): Promise<void> {
    await apiClient.delete(`/social/announcements/${announcementId}`);
  }

  async likeCommunityAnnouncement(
    announcementId: string,
    userId: string,
  ): Promise<CommunityAnnouncement> {
    return apiClient.post<CommunityAnnouncement>(`/social/announcements/${announcementId}/like`, {
      userId,
    });
  }

  // Social Analytics
  async getSocialAnalytics(
    tenantId?: string,
    timeRange?: string,
  ): Promise<{
    totalGroups: number;
    activeGroups: number;
    totalMembers: number;
    totalEvents: number;
    upcomingEvents: number;
    totalPosts: number;
    totalReplies: number;
    totalAnnouncements: number;
    engagementRate: number;
    topGroups: Array<{ groupId: string; name: string; memberCount: number }>;
    topEvents: Array<{ eventId: string; title: string; rsvpCount: number }>;
    activeUsers: Array<{ userId: string; userName: string; activityScore: number }>;
  }> {
    return apiClient.get('/social/analytics', {
      tenantId,
      timeRange,
    });
  }

  // Search
  async searchSocialContent(
    query: string,
    filters?: {
      tenantId?: string;
      contentType?: 'groups' | 'events' | 'posts' | 'announcements';
      category?: string;
    },
  ): Promise<{
    groups: SocialGroup[];
    events: SocialEvent[];
    posts: ForumPost[];
    announcements: CommunityAnnouncement[];
  }> {
    return apiClient.get('/social/search', {
      query,
      ...filters,
    });
  }

  // Forum Categories - Methods expected by hooks
  async getForumCategories(tenantId?: string): Promise<ForumCategory[]> {
    return apiClient.get('/social/forum/categories', { tenantId });
  }

  // Forum Topics - Methods expected by hooks
  async getForumTopics(tenantId?: string, categoryId?: string): Promise<ForumTopic[]> {
    return apiClient.get('/social/forum/topics', { tenantId, categoryId });
  }

  async createForumTopic(
    tenantId: string,
    topicData: { title: string; content: string; categoryId: string; authorId: string },
  ): Promise<ForumTopic> {
    return apiClient.post('/social/forum/topics', { tenantId, ...topicData });
  }

  // Announcements - Method expected by hooks
  async getAnnouncements(
    tenantId?: string,
    filters?: { category?: string; priority?: string; status?: string; authorId?: string },
  ): Promise<CommunityAnnouncement[]> {
    return this.getCommunityAnnouncements(tenantId, filters);
  }

  async createAnnouncement(
    tenantId: string,
    announcementData: Omit<
      CommunityAnnouncement,
      | 'id'
      | 'tenantId'
      | 'viewCount'
      | 'likeCount'
      | 'commentCount'
      | 'notificationSent'
      | 'attachments'
      | 'createdAt'
      | 'updatedAt'
    >,
  ): Promise<CommunityAnnouncement> {
    return apiClient.post('/social/announcements', { tenantId, ...announcementData });
  }
}

export const socialService = new SocialService();
